import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { User, MapPin, Building, Calendar, Loader2, Globe } from "lucide-react";
import type { UseMutationResult } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/formfields";
import type { ProfileDetail, ProfilePayload } from "@/interface/profile";
import type { ApiResponse } from "@/interface/common";

const getTodayDateString = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const personalInfoSchema = z
  .object({
    first_name: z.string().trim().min(1, "First name is required"),
    last_name: z.string().trim().min(1, "Last name is required"),
    birth_date: z.string().trim().optional().nullable(),
    address: z.string().trim().optional(),
    city: z.string().trim().optional(),
    state: z.string().trim().optional(),
    country: z.string().trim().optional(),
    postal_code: z
      .string()
      .trim()
      .regex(/^\d*$/, "Postal code must contain only numbers")
      .optional()
      .nullable(),
  })
  .superRefine((data, ctx) => {
    const todayStr = getTodayDateString();
    if (data.birth_date && data.birth_date.trim() && data.birth_date > todayStr) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Date of Birth cannot be in the future",
        path: ["birth_date"],
      });
    }
  });

export type PersonalInfoFormValues = z.infer<typeof personalInfoSchema>;

interface PersonalInfoSectionProps {
  profile?: ProfileDetail;
  patchProfile?: UseMutationResult<
    ApiResponse<ProfileDetail>,
    Error,
    { id: number | string; payload: Partial<ProfilePayload> }
  >;
}

export function PersonalInfoSection({
  profile,
  patchProfile,
}: PersonalInfoSectionProps) {
  const { control, handleSubmit, reset } = useForm<PersonalInfoFormValues>({
    resolver: zodResolver(personalInfoSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      birth_date: "",
      address: "",
      city: "",
      state: "",
      country: "",
      postal_code: "",
    },
  });

  useEffect(() => {
    if (profile) {
      reset({
        first_name: profile.first_name || "",
        last_name: profile.last_name || "",
        birth_date: profile.birth_date || "",
        address: profile.address || "",
        city: profile.city || "",
        state: profile.state || "",
        country: profile.country || "",
        postal_code: profile.postal_code || "",
      });
    }
  }, [profile, reset]);

  const onSubmit = (data: PersonalInfoFormValues) => {
    if (!profile?.id) return;
    if (patchProfile?.mutate) {
      patchProfile.mutate({ id: profile.id, payload: data });
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm space-y-8"
    >
      <div className="flex justify-between items-center border-b border-slate-100 dark:border-slate-800 pb-4">
        <div>
          <h2 className="text-lg font-bold text-primary">
            Personal Information
          </h2>
          <p className="text-xs text-slate-500">
            Edit candidate personal details
          </p>
        </div>
        <Button
          type="submit"
          disabled={patchProfile?.isPending}
          className="bg-amber-500 hover:bg-amber-600 text-black font-bold border-0 text-xs h-9 px-4"
        >
          {patchProfile?.isPending && (
            <Loader2 className="w-4 h-4 mr-2 animate-spin text-black" />
          )}
          Save Changes
        </Button>
      </div>

      {/* Basic Information */}
      <section className="space-y-4">
        <h3 className="text-xs font-bold text-slate-900 dark:text-slate-200 uppercase tracking-wider">
          Basic Information
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <TextInput
              control={control}
              name="first_name"
              label="First Name"
              required
              prefix={<User className="w-4 h-4 text-slate-400" />}
              placeholder="e.g. Alex"
            />
          </div>
          <div>
            <TextInput
              control={control}
              name="last_name"
              label="Last Name"
              required
              prefix={<User className="w-4 h-4 text-slate-400" />}
              placeholder="e.g. Morgan"
            />
          </div>
          <div>
            <TextInput
              control={control}
              name="birth_date"
              label="Date of Birth"
              type="date"
              max={getTodayDateString()}
              prefix={<Calendar className="w-4 h-4 text-slate-400" />}
            />
          </div>
        </div>
      </section>

      {/* Address Details / Location */}
      <section className="space-y-4 pt-4 border-t border-slate-100 dark:border-slate-800">
        <h3 className="text-xs font-bold text-slate-900 dark:text-slate-200 uppercase tracking-wider">
          Address Details
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <TextInput
              control={control}
              name="address"
              label="Full Address"
              prefix={<MapPin className="w-4 h-4 text-slate-400" />}
              placeholder="123 Main Street, Suite 400"
            />
          </div>
          <div>
            <TextInput
              control={control}
              name="city"
              label="City"
              prefix={<Building className="w-4 h-4 text-slate-400" />}
              placeholder="e.g. San Francisco"
            />
          </div>
          <div>
            <TextInput
              control={control}
              name="state"
              label="State"
              placeholder="e.g. California"
            />
          </div>
          <div>
            <TextInput
              control={control}
              name="country"
              label="Country"
              prefix={<Globe className="w-4 h-4 text-slate-400" />}
              placeholder="e.g. United States"
            />
          </div>
          <div>
            <TextInput
              control={control}
              name="postal_code"
              label="Postal Code"
              integerOnly
              placeholder="e.g. 94103"
            />
          </div>
        </div>
      </section>
    </form>
  );
}
